import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import {
    ChecklistReportBusinessAreasDropdownSettingsModel,
    ChecklistReportBusinessUnitsDropdownSettingsModel,
    ChecklistReportSelectionDropdownSettingsModel,
} from './checklist-report-selection-dropdown-settings';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ChecklistReportService } from '../../checklist-report.service';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { ChecklistReportSelectionDialogComponent } from '../checklist-report-selection-dialog/checklist-report-selection-dialog.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiChecklistReportSaveFilterDialogComponent } from '../checklist-report-save-filter-dialog/checklist-report-save-filter-dialog.component';
import { IChecklistReportBusinessAreaData } from '@fi-app/checklist-report/checklist-report';
import { IChecklistReportBusinessUnitData } from '@fi-app/checklist-report/checklist-report';
import { IChecklistReportSelection } from '@fi-app/checklist-report/checklist-report';
import { IChecklistReportSelectionComponent } from '@fi-app/checklist-report/checklist-report';
import { IChecklistReportSelectionData } from '@fi-app/checklist-report/checklist-report';
import { IChecklistReportSelectionSettings } from '@fi-app/checklist-report/checklist-report';
import {
    FiHierarchicalHelper,
    IFlatItem,
    ISelectSearchSelectedItemChange,
} from '@fi-shared/utils/fi-hierarchical-helper';
import { isDefined } from '@fi-shared/utils';
import { ObjectTypes } from '@fi-shared/types';
import { FiSelectSearchComponent } from '@fi-shared/fi-select-search/select-search.component';

@Component({
    selector: 'fi-checklist-report-selection',
    templateUrl: './checklist-report-selection.component.html',
    styleUrls: ['./checklist-report-selection.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-report-selection' },
    standalone: false,
})
export class ChecklistReportSelectionComponent implements OnInit, IChecklistReportSelectionComponent {
    @Input() checklist: ChecklistModel;
    @Input() selections: IChecklistReportSelection[];
    @Input() businessUnitsData: IChecklistReportBusinessUnitData[];
    @Input() businessAreasData: IChecklistReportBusinessAreaData[];
    @Input() preselect: IChecklistReportSelectionSettings;
    @Output() onSelectionChanged = new EventEmitter<IChecklistReportSelectionData>();
    @ViewChild('businessUnitSelectSearch') businessUnitSelectSearch: FiSelectSearchComponent<number[]>;
    StartDate: Date;
    EndDate: Date;

    selectionsListData: ChecklistReportSelectionDropdownSettingsModel;
    businessUnitsListData: ChecklistReportBusinessUnitsDropdownSettingsModel;
    businessAreasListData: ChecklistReportBusinessAreasDropdownSettingsModel;
    actions: any[];
    businessUnitsHierarchicalHelper = new FiHierarchicalHelper<IChecklistReportBusinessUnitData, string>(
        (item: IChecklistReportBusinessUnitData) => {
            return {
                id: item.IdObjectType,
                name: item.Name,
                childItems: item.ChildItems,
            };
        },
    );
    selectedBusinessUnitIds: string[] = []; // sample {"ObjectId":17,"ObjectType":4} todo

    private nowDate: Date;

    constructor(
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private loadingService: LoadingService,
        private checklistReportsService: ChecklistReportService,
        private stateService: FiStateService,
    ) {}

    ngOnInit() {
        this.nowDate = new Date();
        this.nowDate.setHours(0, 0, 0, 0);
        this.EndDate = this.nowDate;
        this.StartDate = new Date(this.nowDate.getTime());
        this.StartDate.setFullYear(this.StartDate.getFullYear() - 1);

        const skipDefaultInit = !!this.preselect;
        if (!isDefined(this.preselect)) {
            this.preselect = {};
        }

        if (this.preselect.StartDate) {
            this.StartDate = this.preselect.StartDate;
        }

        if (this.preselect.EndDate) {
            this.EndDate = this.preselect.EndDate;
        }

        this.selectionsListData = new ChecklistReportSelectionDropdownSettingsModel(
            this.selections,
            this.preselect.selections,
        );
        this.businessUnitsListData = new ChecklistReportBusinessUnitsDropdownSettingsModel(
            this.businessUnitsData,
            this.preselect.businessUnits,
        );
        this.businessAreasListData = new ChecklistReportBusinessAreasDropdownSettingsModel(
            this.preselect.businessAreas,
        );

        this.updateBusinessAreas(skipDefaultInit);

        let onSelectionChangedInProcess = false;
        this.selectionsListData.onSelectionDataChanged = (shouldUpdateBusinessAreas) => {
            // when we click on business units we should recalculate business areas data set
            if (shouldUpdateBusinessAreas) {
                this.updateBusinessAreas(false);
            }

            // onSelectionDataChanged fires when user chooses selection from saved selections list
            // in this case we should not update current selection
            if (!onSelectionChangedInProcess) {
                // check all selections if we have any one with same set of selection values
                const currentSelectionData: string = JSON.stringify(this.getSelectionData(true));
                const selection: IChecklistReportSelection = this.selectionsListData.dataSet.find(
                    (item: IChecklistReportSelection) => {
                        return currentSelectionData === item.Selection;
                    },
                );
                this.chooseSelection(selection);
            }
        };

        this.selectionsListData.onSelectionChanged = () => {
            onSelectionChangedInProcess = true;
            setTimeout(() => {
                onSelectionChangedInProcess = false;
            });
            let selection = this.selectionsListData.getSelection();
            if (selection) this.chooseSelection(this.selectionsListData.getSelection());
        };

        this.actions = this.getActions(this.checklist.Id, this.selectionsListData);

        if (!skipDefaultInit) this.initDefaultSelection();

        this.businessUnitsHierarchicalHelper.setFlatItems(this.businessUnitsListData.dataSet);
        this.setSelectedBusinessUnitIds(this.businessUnitsListData.getSelectedBusinessUnitIds());
    }

    setSelectedBusinessUnitIds(businessUnitIds: string[]) {
        this.selectedBusinessUnitIds = businessUnitIds;
    }

    selectedBusinessUnitsChange(
        $event: ISelectSearchSelectedItemChange<IFlatItem<IChecklistReportBusinessUnitData, string>>,
    ) {
        // if element is not the only one in the array -> call onSelectedItemChange for all elements
        const currentElements = this.businessUnitsHierarchicalHelper.flatItems.filter(
            (item) => item.Id === $event.item.sourceItem.IdObjectType,
        );

        currentElements.forEach((changedElement) => {
            if (!$event.isSelected) {
                const selectedItemIndex = this.selectedBusinessUnitIds.findIndex((id: string) => {
                    return id === changedElement.Id;
                });
                if (selectedItemIndex !== -1) {
                    this.selectedBusinessUnitIds.splice(selectedItemIndex, 1);
                }
            }
        });

        let result = this.businessUnitsHierarchicalHelper.onSelectedItemChange({
            selectedItemIds: this.selectedBusinessUnitIds,
            changedItem: $event.item,
            isSelected: $event.isSelected,
            selectParentWithOnlyChildSelected: true,
        });

        this.setBusinessUnitsData(result);
    }

    setBusinessUnitsData(value: string[]) {
        this.setSelectedBusinessUnitIds(value);
        this.businessUnitsListData.SelectedModel = this.getSelectedModelByBusinessUnitIds();
        this.selectionsListData.onSelectionDataChanged(true);
    }

    getSelectedModelByBusinessUnitIds(): string[] {
        const result: string[] = [];
        this.selectedBusinessUnitIds.forEach((selectedBusinessUnitId) => {
            const selectedElement = this.businessUnitsHierarchicalHelper.flatItems.find(
                (item) => item.Id === selectedBusinessUnitId,
            );
            result.push(selectedElement.sourceItem.IdObjectType);
        });
        return result;
    }

    private updateBusinessAreas(keepSelection?: boolean) {
        // get only businessAreas, which contains selected business units
        const selectedBusinessUnitIds = this.businessUnitsListData.getSelectedBusinessUnitIds();

        const businessAreasDataSet = this.businessAreasData.filter((businessArea: IChecklistReportBusinessAreaData) => {
            let hasSelectedBusinessUnits = false;
            businessArea.BusinessUnitIds.forEach((id) => {
                if (!hasSelectedBusinessUnits) {
                    hasSelectedBusinessUnits = selectedBusinessUnitIds.indexOf(id) !== -1;
                }
            });
            return hasSelectedBusinessUnits;
        });

        this.businessAreasListData.setDataSet(businessAreasDataSet);

        let deselectedBusinessAreaIds = [];
        if (keepSelection && this.businessAreasListData.SelectedModel.length) {
            //dont keep selection if no areas selected when business units are changing
            deselectedBusinessAreaIds = this.businessAreasListData.dataSet
                .filter((businessArea) => {
                    return this.businessAreasListData.SelectedModel.indexOf(businessArea.Id) === -1;
                })
                .map((businessArea) => {
                    return businessArea.Id;
                });
        }

        // select all business areas except manually deselected
        this.businessAreasListData.SelectedModel = this.businessAreasListData.dataSet
            .map((item) => item[this.businessAreasListData.dataValueField])
            .filter((businessAreaId) => {
                return deselectedBusinessAreaIds.indexOf(businessAreaId) === -1;
            });
    }

    // update start/end date/business units/business areas when chosen selection changed
    private chooseSelection(selection: IChecklistReportSelection, overrideDatesFromStateParams?: boolean) {
        this.selectionsListData.SelectedModel = selection ? selection.Id : null;
        if (selection) {
            const selectionData = JSON.parse(selection.Selection);
            this.StartDate = new Date(selectionData.StartDate);
            this.EndDate = new Date(selectionData.EndDate);

            if (overrideDatesFromStateParams) {
                // on 1st load we should be able set dates from state params
                this.setDatesFromStateParams();
            }

            this.businessUnitsListData.SelectedModel = selectionData.BusinessUnitsSelectedModel;
            this.setSelectedBusinessUnitIds(this.businessUnitsListData.getSelectedBusinessUnitIds());
            this.updateBusinessAreas();
            this.businessAreasListData.SelectedModel = selectionData.BusinessAreasSelectedModel;
        }

        this.emitChange();
    }

    private initDefaultSelection() {
        const defaultSelection = this.selectionsListData.getDefaultSelection();
        if (!defaultSelection) {
            // init default
            if (this.checklist.LastActivePeriodStartDate) {
                this.StartDate = new Date(this.checklist.LastActivePeriodStartDate);
                this.StartDate.setHours(0, 0, 0, 0);
            }

            if (this.checklist.LastActivePeriodEndDate) {
                this.EndDate = new Date(this.checklist.LastActivePeriodEndDate);
                this.EndDate.setHours(0, 0, 0, 0);
                if (this.EndDate > this.nowDate) {
                    this.EndDate = new Date(this.nowDate.getTime());
                }
            } else {
                this.EndDate = new Date(this.nowDate.getTime());
            }

            this.setDatesFromStateParams();

            setTimeout(() => {
                // https://chainformation.atlassian.net/browse/FI-2638
                this.businessUnitSelectSearch.toggleAllSelection();
            });
        } else {
            this.chooseSelection(defaultSelection, true);
        }
    }

    private setDatesFromStateParams() {
        if (this.stateService.params.startDate) {
            this.StartDate = new Date(decodeURIComponent(this.stateService.params.startDate));
            this.StartDate.setHours(0, 0, 0, 0);
        }
        if (this.stateService.params.endDate) {
            this.EndDate = new Date(decodeURIComponent(this.stateService.params.endDate));
            this.EndDate.setHours(0, 0, 0, 0);
        }
    }

    private getSelectionData(isForSave?: boolean) {
        const data: IChecklistReportSelectionData = {
            StartDate: this.StartDate,
            EndDate: this.EndDate,
        };
        if (isForSave) {
            data.BusinessUnitsSelectedModel = this.businessUnitsListData.SelectedModel.sort(comparator((item) => item));
            data.BusinessAreasSelectedModel = this.businessAreasListData.SelectedModel.sort(comparator((item) => item));
        } else {
            data.BusinessUnitIds = this.businessUnitsListData.getSelectedBusinessUnitIds();
        }
        return data;
    }

    private saveSelection(checklistId: number, selectionsListData: ChecklistReportSelectionDropdownSettingsModel) {
        return new Promise<IChecklistReportSelection>((resolve, reject) => {
            this.fiDialogsService
                .custom({
                    hideHeader: true,
                    yesButtonText: this.appTextService.get('common.save'),
                    contentComponentRef: FiChecklistReportSaveFilterDialogComponent,
                })
                .onYesButtonClick((result) => {
                    const selection = <IChecklistReportSelection>{
                        Id: undefined,
                        Selection: JSON.stringify(this.getSelectionData(true)),
                        IsDefault: false,
                        Name: result,
                    };

                    this.loadingService.trackPromise(
                        this.checklistReportsService
                            .saveCheckListReportSelection(checklistId, selection)
                            .then((selectionId: number) => {
                                selection.Id = selectionId;
                                selectionsListData.setDataSet(selectionsListData.dataSet.concat([selection]));
                                selectionsListData.SelectedModel = selection.Id;
                                resolve(selection);
                            }, reject),
                    );
                })
                .onNoButtonClick(reject);
        });
    }

    private updateIsDefault(
        selection: IChecklistReportSelection,
        isDefault: boolean,
        selections: IChecklistReportSelection[],
    ) {
        this.loadingService
            .trackPromise(this.checklistReportsService.updateCheckListReportSelectionIsDefault(selection.Id, isDefault))
            .then((result: boolean) => {
                if (result) {
                    // clear all default selections
                    selections.forEach((item: IChecklistReportSelection) => {
                        item.IsDefault = false;
                    });
                }
                selection.IsDefault = result;
            });
    }

    getFilteredActions() {
        return this.actions.filter((item) => !item.condition || item.condition(this.checklist));
    }

    private getActions(checklistId: number, selectionsListData: ChecklistReportSelectionDropdownSettingsModel) {
        return [
            {
                title: 'set_default',
                condition: () => {
                    return !selectionsListData.isSelectionDefault();
                },
                onClick: () => {
                    const selection = selectionsListData.getSelection();
                    if (!selection) {
                        this.saveSelection(checklistId, selectionsListData).then(
                            (section: IChecklistReportSelection) => {
                                this.updateIsDefault(section, true, selectionsListData.dataSet);
                            },
                        );
                    } else {
                        this.updateIsDefault(selection, true, selectionsListData.dataSet);
                    }
                },
                infoTooltip: 'checklist.report_selection_set_default',
                iconClass: {
                    'fa-thumb-tack': true,
                },
            },
            {
                title: 'unset_default',
                condition: () => {
                    return selectionsListData.isSelectionDefault();
                },
                onClick: () => {
                    const selection = selectionsListData.getSelection();
                    this.updateIsDefault(selection, false, selectionsListData.dataSet);
                },
                iconClass: {
                    'fa-thumb-tack': true,
                    BrandedColor: true,
                },
            },
            {
                title: 'save_filter',
                isDisabled: () => {
                    return selectionsListData.isSelectionSaved();
                },
                onClick: () => {
                    this.saveSelection(checklistId, selectionsListData);
                },
                infoTooltip: 'checklist.report_selection_save_filter',
                iconClass: {
                    'fa-floppy-o': true,
                },
            },
            {
                title: 'delete_filter',
                isDisabled: () => {
                    return !selectionsListData.isSelectionSaved();
                },
                onClick: () => {
                    const selection = selectionsListData.getSelection();
                    if (!selection) return;

                    this.fiDialogsService
                        .confirm({
                            headerText: this.appTextService.get('checklist_reports.remove_filter_confirm_header'),
                            messageText: this.appTextService.get('checklist_reports.remove_filter_confirm'),
                        })
                        .onYesButtonClick(() => {
                            this.loadingService.trackPromise(
                                this.checklistReportsService.deleteCheckListReportSelection(selection.Id).then(() => {
                                    let newDataSet = selectionsListData.dataSet.slice();
                                    const idx = newDataSet.indexOf(selection);
                                    newDataSet.splice(idx, 1);
                                    selectionsListData.setDataSet(newDataSet);
                                }),
                            );
                        });
                },
                iconClass: {
                    'fa-trash-o': true,
                },
            },
        ];
    }

    private emitChange() {
        this.onSelectionChanged.emit(this.getSelectionData());
    }

    openMobileSelection() {
        this.fiDialogsService
            .custom({
                hideHeader: true,
                yesButtonText: this.appTextService.get('common.save'),
                contentComponentRef: ChecklistReportSelectionDialogComponent,
                contentComponentData: {
                    checklist: this.checklist,
                    selections: this.selections,
                    businessUnitsData: this.businessUnitsData,
                    businessAreasData: this.businessAreasData,
                    currentSelection: <IChecklistReportSelectionSettings>{
                        StartDate: new Date(this.StartDate),
                        EndDate: new Date(this.EndDate),
                        selections: this.selectionsListData.SelectedModel,
                        businessUnits: this.businessUnitsListData.SelectedModel.slice(),
                        businessAreas: this.businessAreasListData.SelectedModel.slice(),
                    },
                },
                config: {
                    width: '400px',
                    panelClass: 'checklist-report-selection-dialog',
                },
            })
            .onYesButtonClick((result) => {
                this.StartDate = result.startDate;
                this.EndDate = result.endDate;
                this.selectionsListData.SelectedModel = result.selectionsListDataModel;
                this.businessUnitsListData.SelectedModel = result.businessUnitsListModel;
                this.businessAreasListData.SelectedModel = result.businessAreasListModel;
                this.emitChange();
            });
    }
}
